import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { authAction, authSelector } from '../features/auth/slice';
import { Lock, LogIn, ShieldCheck, User } from 'lucide-react';

const BG_IMAGES = [
  'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=800&q=40',
  'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&w=800&q=40',
  'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?auto=format&fit=crop&w=800&q=40',
  'https://images.unsplash.com/photo-1507838153414-b4b713384a76?auto=format&fit=crop&w=800&q=40',
  'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?auto=format&fit=crop&w=800&q=40',
  'https://images.unsplash.com/photo-1524368535928-5b5e00ddc76b?auto=format&fit=crop&w=800&q=40',
  'https://images.unsplash.com/photo-1458560871784-56d23406c091?auto=format&fit=crop&w=800&q=40',
  'https://images.unsplash.com/photo-1485579149621-3123dd979885?auto=format&fit=crop&w=800&q=40',
  'https://images.unsplash.com/photo-1571330735066-03aaa9429d89?auto=format&fit=crop&w=800&q=40',
  'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?auto=format&fit=crop&w=800&q=40',
];

// hostname에서 slug 추출: pink.kdist.net → 'pink'
function getSlug() {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  // *.kdist.net 형태
  if (parts.length >= 3 && parts.slice(-2).join('.') === 'kdist.net') {
    return parts[0];
  }
  return null;
}

export default function DistLoginPage() {
  const bgImage = useMemo(() => BG_IMAGES[Math.floor(Math.random() * BG_IMAGES.length)], []);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { me } = useSelector(authSelector.selectAuthState);

  const slug = getSlug();
  const [dist, setDist] = useState(null);   // { id, name, logo, theme }
  const [distLoading, setDistLoading] = useState(true);

  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [otpStep, setOtpStep] = useState(false);
  const [otpLoginId, setOtpLoginId] = useState('');
  const [otp, setOtp] = useState('');
  const otpInputRef = useRef(null);

  // 유통사 정보 로드 + localStorage 캐시 선저장 (dashboard 진입 시 플래시 방지)
  useEffect(() => {
    if (!slug) { setDistLoading(false); return; }
    axios.get(`${process.env.REACT_APP_API_URL}/api/distributors/by-slug/${slug}`)
      .then(res => {
        setDist(res.data);
        try {
          const cacheKey = `dist_info_${window.location.hostname}`;
          localStorage.setItem(cacheKey, JSON.stringify({
            distributorId: res.data.id,
            distributorTheme: res.data.theme || 'dark',
            distributorName: res.data.name,
            distributorLogo: res.data.logo || null,
            distributorBusinessNumber: null,
            distributorRepresentative: null,
            distributorAddress: null,
          }));
        } catch {}
      })
      .catch(() => setDist(null))
      .finally(() => setDistLoading(false));
  }, [slug]);

  // dist 로드 시 파비콘 + title + OG 태그 적용
  useEffect(() => {
    if (!dist) return;
    const name = dist.name || '정산시스템';
    document.title = `${name} 정산시스템`;
    const kdistSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none"><rect width="32" height="32" rx="8" fill="#0ea5e9"/><rect x="5" y="19" width="5" height="8" rx="1.5" fill="white" fill-opacity="0.55"/><rect x="13.5" y="14" width="5" height="13" rx="1.5" fill="white"/><rect x="22" y="9" width="5" height="18" rx="1.5" fill="white" fill-opacity="0.55"/><path d="M7.5 19 L16 13.5 L24.5 8.5" stroke="white" stroke-width="1.6" stroke-linecap="round"/><circle cx="7.5" cy="19" r="1.8" fill="white"/><circle cx="16" cy="13.5" r="1.8" fill="white"/><circle cx="24.5" cy="8.5" r="2.2" fill="white"/></svg>`;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    link.href = `data:image/svg+xml,${encodeURIComponent(kdistSvg)}`;
    const setMeta = (property, content) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('property', property); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('og:title', `${name} 정산시스템`);
    setMeta('og:description', `${name} 음원 정산시스템`);
    setMeta('og:site_name', name);
  }, [dist]);

  // 이미 로그인 상태면 대시보드로
  useEffect(() => {
    if (me) navigate('/dashboard', { replace: true });
  }, [me, navigate]);

  const handleLoginSuccess = (token) => {
    // 이전 세션 상태 완전 초기화 (다른 유통사로 전환 시 stale 데이터 방지)
    dispatch(authAction.logout());
    localStorage.setItem('token', token);
    axios.get('/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      dispatch(authAction.changeMe(res.data));
      navigate('/dashboard', { replace: true });
    }).catch(err => {
      alert('로그인 후 사용자 정보를 불러오지 못했습니다: ' + (err.response?.data?.message || err.message));
    });
  };

  const doLogin = () => {
    axios.post(`${process.env.REACT_APP_API_URL}/auth/login`, { loginId, password, slug })
      .then(res => {
        if (res.data.token) {
          handleLoginSuccess(res.data.token);
        } else if (res.data.requireOtp) {
          setOtpLoginId(res.data.loginId);
          setOtpStep(true);
          setTimeout(() => otpInputRef.current?.focus(), 100);
        } else {
          alert(res.data.message || '아이디/비밀번호를 확인하세요.');
        }
      })
      .catch(err => alert(err.response?.data?.message || '로그인에 실패했습니다.'));
  };

  const doVerifyOtp = () => {
    axios.post(`${process.env.REACT_APP_API_URL}/auth/verify-otp`, { loginId: otpLoginId, otp })
      .then(res => {
        if (res.data.token) handleLoginSuccess(res.data.token);
        else alert(res.data.message || '인증 실패');
      })
      .catch(err => alert(err.response?.data?.message || '인증에 실패했습니다.'));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (otpStep) doVerifyOtp();
    else doLogin();
  };

  const isDark = dist?.theme === 'dark';

  if (distLoading) {
    return (
      <div className="min-h-dvh bg-[#0a0f1e] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-sky-500" />
      </div>
    );
  }

  // 유통사를 찾을 수 없을 때
  if (!dist) {
    return (
      <div className="min-h-dvh bg-[#0a0f1e] flex flex-col items-center justify-center gap-4 px-4">
        <p className="text-slate-400 text-sm">서비스를 찾을 수 없습니다.</p>
        <p className="text-slate-600 text-xs">{slug}</p>
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex flex-col items-center justify-center px-4 py-12 relative">
      {/* 전체화면 배경 이미지 */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${bgImage})`, filter: 'brightness(0.45) saturate(0.7)' }}>
      </div>
      <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(10,20,60,0.55) 0%, rgba(30,10,50,0.45) 100%)' }} />
      <div className="relative z-10 w-full flex flex-col items-center justify-center">

      <div className="w-full max-w-sm">
      {/* 브랜드 헤더 */}
        <div className="flex flex-col items-center mb-8 gap-3">
          {dist.logo
            ? <img src={dist.logo} alt={dist.name} className="h-12 object-contain" />
            : <div className="text-2xl font-bold text-white">{dist.name}</div>}
          <p className="text-sm text-white/50">정산시스템에 오신 것을 환영합니다</p>
        </div>

        {/* 카드 */}
        <div
          className="rounded-2xl border shadow-xl overflow-hidden"
          style={{
            background: isDark ? '#0d1526' : '#ffffff',
            borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
          }}>

          <div
            className="px-6 pt-6 pb-4 border-b"
            style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
            <h1 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
              권리사 로그인
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-6 space-y-4">
            {otpStep ? (
              <>
                <div className="flex flex-col items-center gap-2 py-2">
                  <ShieldCheck size={36} className="text-sky-400" />
                  <p className={`font-semibold text-base ${isDark ? 'text-white' : 'text-slate-800'}`}>이메일 인증</p>
                  <p className={`text-sm text-center ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    등록된 이메일로 발송된<br />6자리 인증코드를 입력하세요.
                  </p>
                </div>
                <input
                  ref={otpInputRef}
                  type="text"
                  maxLength={6}
                  inputMode="numeric"
                  className="w-full px-4 py-3 rounded-xl border text-lg text-center font-bold tracking-widest outline-none focus:ring-2 focus:ring-sky-500/30 transition"
                  style={{
                    background: isDark ? '#1a2b42' : '#f8fafc',
                    borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)',
                    color: isDark ? '#e2e8f0' : '#1e293b',
                  }}
                  placeholder="000000"
                  value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
                <button type="submit" className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold rounded-xl transition-colors">
                  <ShieldCheck size={16} /> 인증 완료
                </button>
                <button type="button" className={`w-full text-center text-xs transition-colors ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
                  onClick={() => { setOtpStep(false); setOtp(''); }}>
                  ← 로그인으로 돌아가기
                </button>
              </>
            ) : (
              <>
                <div className="space-y-1.5">
                  <label className={`text-xs font-semibold uppercase tracking-wide block ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>아이디</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <User size={15} className={isDark ? 'text-slate-500' : 'text-slate-400'} />
                    </div>
                    <input
                      type="text"
                      className="w-full pl-9 pr-4 py-2.5 rounded-xl border outline-none focus:ring-2 focus:ring-sky-500/30 transition text-sm"
                      style={{
                        background: isDark ? '#1a2b42' : '#f8fafc',
                        borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)',
                        color: isDark ? '#e2e8f0' : '#1e293b',
                      }}
                      placeholder="예) P0001"
                      value={loginId}
                      onChange={e => setLoginId(e.target.value)}
                      autoComplete="username"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className={`text-xs font-semibold uppercase tracking-wide block ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>비밀번호</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                      <Lock size={15} className={isDark ? 'text-slate-500' : 'text-slate-400'} />
                    </div>
                    <input
                      type="password"
                      className="w-full pl-9 pr-4 py-2.5 rounded-xl border outline-none focus:ring-2 focus:ring-sky-500/30 transition text-sm"
                      style={{
                        background: isDark ? '#1a2b42' : '#f8fafc',
                        borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)',
                        color: isDark ? '#e2e8f0' : '#1e293b',
                      }}
                      placeholder="비밀번호"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      autoComplete="current-password"
                    />
                  </div>
                </div>

                <button type="submit"
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold rounded-xl transition-colors">
                  <LogIn size={16} /> 로그인
                </button>

                <p className={`text-center text-xs pt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  최초 비밀번호는 전화번호 뒤 4자리이며<br />분실시 담당자에게 문의 해주세요.
                </p>
              </>
            )}
          </form>
        </div>

        {/* 하단 powered by */}
        <p className={`text-center text-xs mt-6 ${isDark ? 'text-slate-600' : 'text-white/40'}`}>
          Powered by <span className="font-semibold">KDIST</span>
        </p>
      </div>
      </div>
    </div>
  );
}
